import { useState, useEffect } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import { motion } from "framer-motion";
import api, { formatApiErrorDetail } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { SoccerBall, EnvelopeSimple, Lock, GoogleLogo, EnvelopeOpen } from "@phosphor-icons/react";

const HERO =
  "https://customer-assets-m6fa6gv7.emergentagent.net/job_coach-hub-312/artifacts/bu3sobws_The%20Gaffer%20welcome%20page.webp";
const SUPPORT_EMAIL = "NewmarketSC2013girls@gmail.com";

export default function Login() {
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [error, setError] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const { setUser } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();

  useEffect(() => {
    const params = new URLSearchParams(location.search);
    const err = params.get("error");
    if (err) setError(err);
  }, [location.search]);

  const submit = async (e) => {
    e.preventDefault();
    setError("");
    setSubmitting(true);
    try {
      const { data } = await api.post("/auth/login", { email, password });
      setUser(data);
      navigate("/dashboard");
    } catch (err) {
      setError(formatApiErrorDetail(err.response?.data?.detail) || err.message);
    } finally {
      setSubmitting(false);
    }
  };

  const googleLogin = () => {
    // REMINDER: DO NOT HARDCODE THE URL, OR ADD ANY FALLBACKS OR REDIRECT URLS, THIS BREAKS THE AUTH
    const redirectUrl = window.location.origin + "/dashboard";
    window.location.href = `https://auth.emergentagent.com/?redirect=${encodeURIComponent(redirectUrl)}`;
  };

  return (
    <div className="min-h-screen flex bg-[#0A0A0A]">
      {/* Hero */}
      <div className="hidden lg:block relative w-1/2 overflow-hidden">
        <img src={HERO} alt="Youth soccer" className="absolute inset-0 w-full h-full object-cover" />
        <div className="absolute inset-0 bg-gradient-to-t from-black via-black/50 to-black/20" />
        <div className="absolute bottom-0 left-0 p-12 z-10">
          <div className="flex items-center gap-3 mb-6">
            <img src="/logo-full.png" alt="The Gaffer" className="w-24 h-24 object-contain" />
          </div>
          <h1 className="font-heading text-6xl text-white leading-[0.95] max-w-md">
            Run your team like a <span className="text-[#CCFF00]">pro.</span>
          </h1>
          <p className="text-zinc-400 mt-4 max-w-sm text-base">
            View Games, Track results and Statistic's
          </p>
        </div>
      </div>

      {/* Form */}
      <div className="flex-1 flex items-center justify-center p-6 sm:p-12">
        <motion.div
          initial={{ opacity: 0, y: 12 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.4 }}
          className="w-full max-w-md"
        >
          <div className="lg:hidden flex items-center gap-2 mb-8 justify-center">
            <img src="/logo-icon.png" alt="The Gaffer" className="w-10 h-10 object-contain" />
            <span className="font-heading text-2xl text-white pt-1">THE <span className="text-[#CCFF00]">GAFFER</span></span>
          </div>

          <h2 className="font-heading text-4xl text-white mb-2">
            Welcome back
          </h2>
          <p className="text-zinc-500 mb-8 text-sm">
            Sign in to your team dashboard.
          </p>

          <button
            onClick={googleLogin}
            data-testid="google-login-btn"
            className="w-full flex items-center justify-center gap-3 bg-white text-black font-bold py-3 rounded-sm hover:bg-zinc-200 transition-colors duration-150 mb-6"
          >
            <GoogleLogo size={20} weight="bold" />
            Continue with Google
          </button>

          <div className="flex items-center gap-4 mb-6">
            <div className="flex-1 h-px bg-zinc-800" />
            <span className="text-xs text-zinc-600 font-label">or</span>
            <div className="flex-1 h-px bg-zinc-800" />
          </div>

          <form onSubmit={submit} className="space-y-4">
            <div>
              <label className="font-label text-xs text-zinc-400 block mb-2">Email or username</label>
              <div className="relative">
                <EnvelopeSimple size={18} className="absolute left-3 top-1/2 -translate-y-1/2 text-zinc-500" />
                <input
                  type="text"
                  required
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  data-testid="email-input"
                  placeholder="you@email.com"
                  className="w-full bg-[#121212] border border-zinc-800 text-white pl-10 pr-4 py-3 rounded-sm focus:outline-none focus:border-[#CCFF00] transition-colors duration-150"
                />
              </div>
            </div>
            <div>
              <label className="font-label text-xs text-zinc-400 block mb-2">Password</label>
              <div className="relative">
                <Lock size={18} className="absolute left-3 top-1/2 -translate-y-1/2 text-zinc-500" />
                <input
                  type="password"
                  required
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  data-testid="password-input"
                  placeholder="••••••••"
                  className="w-full bg-[#121212] border border-zinc-800 text-white pl-10 pr-4 py-3 rounded-sm focus:outline-none focus:border-[#CCFF00] transition-colors duration-150"
                />
              </div>
            </div>

            {error && (
              <p className="text-[#FF3B30] text-sm" data-testid="auth-error">
                {error}
              </p>
            )}

            <button
              type="submit"
              disabled={submitting}
              data-testid="auth-submit-btn"
              className="w-full bg-[#CCFF00] text-black font-label py-3 rounded-sm hover:bg-[#B3E600] hover:-translate-y-0.5 transition-all duration-150 disabled:opacity-50 disabled:translate-y-0"
            >
              {submitting ? "Please wait…" : "Sign in"}
            </button>
          </form>

          <a
            href={`mailto:${SUPPORT_EMAIL}`}
            data-testid="contact-team-manager-link"
            className="mt-6 flex items-center justify-center gap-2 text-center text-sm text-zinc-500 hover:text-[#CCFF00] transition-colors duration-150"
          >
            <EnvelopeOpen size={16} weight="bold" />
            Contact Team Manager for Access
          </a>
        </motion.div>
      </div>
    </div>
  );
}
